import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import Calendar from 'react-calendar';
import {
    Header, Button, Dropdown, Input, Label, Icon, Grid, Segment
} from 'semantic-ui-react';
import Select, { SingleValue } from 'react-select';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useStore } from '../../../app/stores/store';
import { sportOptions, createSportOptions } from '../../../app/common/options/sportOptions';
import { PagingParams } from '../../../app/models/Pagination';

interface Props {
    onSearch: (replace: boolean) => void;
}

export default observer(function CourtFilters({ onSearch }: Props) {
    const { t } = useTranslation();
    const { courtStore, userStore } = useStore();

    // ── Mobile detection ──────────────────────────────────────────────────────
    const [isMobile, setIsMobile] = useState(false);
    useEffect(() => {
        const check = () => {
            setIsMobile('ontouchstart' in window && window.innerWidth <= 1024);
        };
        check();
        window.addEventListener('resize', check);
        return () => window.removeEventListener('resize', check);
    }, []);

    const [isExpanded, setIsExpanded] = useState(false);

    // ── Localised sport options ───────────────────────────────────────────────
    const localizedSportOptions = createSportOptions(t, sportOptions);

    const sortFieldOptions = [
        { key: 'distance', text: t('courtFilters.sortFields.distance'), value: 'distance' },
        { key: 'price',    text: t('courtFilters.sortFields.price'),    value: 'price'    },
        { key: 'name',     text: t('courtFilters.sortFields.name'),     value: 'name'     },
    ];
    const sortDirectionOptions = [
        { key: 'asc',  text: t('courtFilters.sortDirections.ascending'),  value: 'asc'  },
        { key: 'desc', text: t('courtFilters.sortDirections.descending'), value: 'desc' },
    ];

    // ── Local UI state ────────────────────────────────────────────────────────
    const [selectedSport, setSelectedSport] = useState<SingleValue<{ value: string; label: string }>>(null);
    const [selectedDate, setSelectedDate]   = useState<Date>(new Date());
    const [distance, setDistance]           = useState(50);
    const [sortField, setSortField]         = useState<string | undefined>(undefined);
    const [sortDir, setSortDir]             = useState<string>('asc');
    const [showCalendar, setShowCalendar]   = useState(false);

    // Start as true so the Search button is enabled immediately when the tab
    // loads — users should not need to change a filter just to trigger the
    // first search.
    const [filterChanged, setFilterChanged] = useState(true);

    const isUserLocationSet =
        userStore.user &&
        (userStore.user.latitude !== 0 || userStore.user.longitude !== 0);

    // ── Apply / search ────────────────────────────────────────────────────────
    const handleSearch = () => {
        courtStore.setSportID(selectedSport?.value ?? null);
        courtStore.setSearchDate(selectedDate);
        courtStore.setRadiusKm(distance);
        courtStore.setSortField(sortField);
        courtStore.setSortDirection(sortDir);
        courtStore.setPagingParams(new PagingParams(1));

        setFilterChanged(false);
        onSearch(true); // replace = true → full reload
        if (isMobile) setIsExpanded(false);
    };

    const handleReset = () => {
        setSelectedSport(null);
        setSelectedDate(new Date());
        setDistance(50);
        setSortField(undefined);
        setSortDir('asc');
        setFilterChanged(true);
        setShowCalendar(false);
    };

    const mark = () => setFilterChanged(true);

    const hasActiveFilters =
        selectedSport ||
        sortField ||
        selectedDate.toDateString() !== new Date().toDateString();

    // ── Mobile collapsed view ─────────────────────────────────────────────────
    if (isMobile && !isExpanded) {
        return (
            <Segment style={{ marginBottom: '5px', padding: '8px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Icon name='building' color='teal' />
                        <span style={{ fontSize: '0.9rem', fontWeight: 'bold' }}>
                            {t('courtFilters.title')}
                        </span>
                        {hasActiveFilters && (
                            <Label size='mini' color='teal' circular>!</Label>
                        )}
                    </div>
                    <div style={{ display: 'flex', gap: '4px' }}>
                        {/* Not disabled on mobile collapsed either */}
                        <Button color='teal' onClick={handleSearch} disabled={!filterChanged} size='mini' compact>
                            <Icon name='search' />
                        </Button>
                        <Button onClick={() => setIsExpanded(true)} size='mini' compact>
                            <Icon name='angle down' />
                        </Button>
                    </div>
                </div>
            </Segment>
        );
    }

    // ── Full filter panel ─────────────────────────────────────────────────────
    return (
        <Segment style={{ marginBottom: isMobile ? '5px' : '10px', padding: isMobile ? '6px' : '10px' }}>
            <Header as='h4' attached='top' color='teal' style={{
                padding: isMobile ? '4px 6px' : '6px 8px',
                margin: 0,
                fontSize: isMobile ? '0.9rem' : '1rem'
            }}>
                <Icon name='building' />
                {t('courtFilters.title')}
                {isMobile && (
                    <Button floated='right' size='mini' compact onClick={() => setIsExpanded(false)} style={{ marginTop: '-2px' }}>
                        <Icon name='angle up' />
                    </Button>
                )}
            </Header>

            <div style={{ padding: isMobile ? '6px' : '8px' }}>

                {/* ── Row 1: Sport + Sort ─────────────────────────────────── */}
                {isMobile ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '6px' }}>
                        <div style={{ fontSize: '0.75rem' }}>
                            <Select
                                options={localizedSportOptions}
                                value={selectedSport}
                                onChange={opt => { setSelectedSport(opt); mark(); }}
                                placeholder={t('courtFilters.placeholders.selectSport')}
                                isSearchable
                                isClearable
                                styles={{
                                    control: base => ({ ...base, minHeight: '24px', fontSize: '0.75rem' }),
                                    menu:    base => ({ ...base, fontSize: '0.75rem' }),
                                }}
                            />
                        </div>
                        <div style={{ display: 'flex', gap: '4px' }}>
                            <div style={{ flex: 1 }}>
                                <Dropdown fluid selection options={sortFieldOptions}
                                    placeholder={t('courtFilters.placeholders.sortBy')}
                                    value={sortField || ''} clearable
                                    onChange={(_, { value }) => { setSortField(value as string); mark(); }}
                                    style={{ fontSize: '0.75rem', minHeight: '24px' }}
                                />
                            </div>
                            <div style={{ flex: '0 0 60px' }}>
                                <Dropdown fluid selection options={sortDirectionOptions}
                                    value={sortDir} disabled={!sortField}
                                    onChange={(_, { value }) => { setSortDir(value as string); mark(); }}
                                    style={{ fontSize: '0.75rem', minHeight: '24px' }}
                                />
                            </div>
                        </div>
                    </div>
                ) : (
                    <Grid columns={4} stackable style={{ marginBottom: '8px' }}>
                        <Grid.Column width={5}>
                            <div style={{ fontSize: '0.8rem' }}>
                                <Select
                                    options={localizedSportOptions}
                                    value={selectedSport}
                                    onChange={opt => { setSelectedSport(opt); mark(); }}
                                    placeholder={t('courtFilters.placeholders.selectSport')}
                                    isSearchable isClearable
                                    styles={{
                                        control: base => ({ ...base, minHeight: '28px', fontSize: '0.8rem' }),
                                        menu:    base => ({ ...base, fontSize: '0.8rem' }),
                                    }}
                                />
                            </div>
                        </Grid.Column>
                        <Grid.Column width={4}>
                            <Dropdown fluid selection options={sortFieldOptions}
                                placeholder={t('courtFilters.placeholders.sortBy')}
                                value={sortField || ''} clearable
                                onChange={(_, { value }) => { setSortField(value as string); mark(); }}
                                style={{ fontSize: '0.8rem', minHeight: '28px' }}
                            />
                        </Grid.Column>
                        <Grid.Column width={3}>
                            <Dropdown fluid selection options={sortDirectionOptions}
                                value={sortDir} disabled={!sortField}
                                onChange={(_, { value }) => { setSortDir(value as string); mark(); }}
                                style={{ fontSize: '0.8rem', minHeight: '28px' }}
                            />
                        </Grid.Column>
                    </Grid>
                )}

                {/* ── Row 2: Date + Distance + Buttons ───────────────────── */}
                <div style={{
                    display: 'flex', alignItems: 'center',
                    gap: isMobile ? '6px' : '10px',
                    flexWrap: 'wrap',
                    fontSize: isMobile ? '0.75rem' : '0.8rem'
                }}>
                    {/* Date picker button */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                        <span style={{ fontWeight: 'bold', fontSize: isMobile ? '0.75rem' : '0.8rem' }}>
                            {t('courtFilters.date')}:
                        </span>
                        <Button size='mini' compact
                            color={showCalendar ? 'teal' : undefined}
                            onClick={() => setShowCalendar(s => !s)}
                            style={{ fontSize: isMobile ? '0.7rem' : '0.8rem', padding: isMobile ? '4px 6px' : '6px 8px' }}
                        >
                            <Icon name='calendar' />
                            {selectedDate.toLocaleDateString()}
                        </Button>
                    </div>

                    {/* Distance slider */}
                    <div style={{
                        display: 'flex', alignItems: 'center', gap: '3px',
                        minWidth: isMobile ? '120px' : '150px', flex: 1
                    }}>
                        <span style={{ fontWeight: 'bold', whiteSpace: 'nowrap', fontSize: isMobile ? '0.7rem' : '0.8rem' }}>
                            {t('courtFilters.distance')}: {distance}km
                        </span>
                        <Input type='range' min={0} max={300} step={1} value={distance}
                            disabled={!isUserLocationSet}
                            onChange={e => { setDistance(Number(e.target.value)); mark(); }}
                            style={{ flex: 1, maxWidth: isMobile ? '80px' : '100px' }}
                        />
                        {!isUserLocationSet && userStore.user && (
                            <Link to={`/profiles/${userStore.user?.userID}`}>
                                <Label color='red' size='mini'>
                                    <Icon name='location arrow' />
                                    {t('courtFilters.setLocation')}
                                </Label>
                            </Link>
                        )}
                    </div>

                    {/* Action buttons */}
                    <div style={{ display: 'flex', gap: isMobile ? '4px' : '5px', flexShrink: 0 }}>
                        <Button color='teal' onClick={handleSearch} disabled={!filterChanged}
                            size={isMobile ? 'small' : 'mini'} compact={!isMobile}
                            style={{ whiteSpace: 'nowrap' }}
                        >
                            <Icon name='search' />
                            {t('courtFilters.applyFilters')}
                        </Button>
                        <Button basic onClick={handleReset}
                            size={isMobile ? 'small' : 'mini'} compact={!isMobile}
                            style={{ whiteSpace: 'nowrap' }}
                        >
                            <Icon name='undo' />
                            {t('courtFilters.reset')}
                        </Button>
                    </div>
                </div>

                {/* ── Collapsible Calendar ────────────────────────────────── */}
                {showCalendar && (
                    <div style={{ marginTop: isMobile ? '6px' : '10px', maxWidth: isMobile ? '280px' : '300px' }}>
                        <Calendar
                            onChange={date => {
                                setSelectedDate(date as Date);
                                mark();
                                setShowCalendar(false);
                            }}
                            value={selectedDate}
                            minDate={new Date()}
                            className='compact-calendar'
                        />
                        <style>{`
                            .compact-calendar { font-size: ${isMobile ? '0.6rem' : '0.7rem'} !important; max-width: ${isMobile ? '280px' : '300px'} !important; }
                            .compact-calendar .react-calendar__tile { height: ${isMobile ? '22px' : '25px'} !important; padding: 0px 2px !important; font-size: ${isMobile ? '0.6rem' : '0.7rem'} !important; display: flex; align-items: center; justify-content: center; }
                            .compact-calendar .react-calendar__navigation { height: ${isMobile ? '24px' : '28px'} !important; }
                            .compact-calendar .react-calendar__navigation button { font-size: ${isMobile ? '0.7rem' : '0.75rem'} !important; padding: 0 !important; height: ${isMobile ? '24px' : '28px'} !important; }
                        `}</style>
                    </div>
                )}
            </div>
        </Segment>
    );
});